/**
 * Manchester Triage System - Rules Implementation
 * 
 * This file contains the actual implementation of Manchester Triage System rules
 * based on the presentations and discriminators defined in the system.
 * 
 * Each rule checks specific patient data against clinical criteria and determines
 * if a particular discriminator applies, which then sets the urgency level.
 * 
 * Reference: Manchester Triage Group. Emergency Triage: Manchester Triage Group.
 * 3rd ed. Wiley-Blackwell; 2014.
 */

import { TriageRule, TriageData, Comparator } from '@/types/triage';
import { containsPhrase, containsAnyPhrase, containsAllPhrases } from '../textProcessing';

/**
 * Core Manchester Triage System rules
 * Each rule implements a specific discriminator for a specific presentation
 */
// Geriatric-specific rules
export const GERIATRIC_RULES: TriageRule[] = [
  {
    id: 'MTS-RULE-G001',
    name: 'Fall in Elderly with Risk Factors',
    description: 'Elderly patient (≥75 years) with a fall and presence of risk factors',
    category: 'mechanism',
    condition: {
      parameter: 'additionalInfo',
      comparator: 'includes',
      value: 'fall',
    },
    impact: {
      urgency: 'urgent', // Maps to 'Very Urgent' in MTS display
      description: 'Elderly patient with fall and risk factors requires rapid assessment',
      reference: 'MTS v3 Geriatric Considerations'
    },
    presentingComplaint: 'Falls',
    discriminator: 'High-Risk Fall in Elderly',
    ageConstraint: {
      min: 75
    }
  },
  {
    id: 'MTS-RULE-G002',
    name: 'Acute Confusion in Elderly',
    description: 'Elderly patient (≥75 years) with new onset confusion or significant change from baseline',
    category: 'symptom',
    condition: {
      parameter: 'additionalInfo',
      comparator: 'includes',
      value: 'confusion',
    },
    impact: {
      urgency: 'urgent', // Maps to 'Very Urgent' in MTS display
      description: 'Elderly patient with acute confusion requires rapid assessment',
      reference: 'MTS v3 Geriatric Considerations'
    },
    presentingComplaint: 'Altered Mental Status',
    discriminator: 'Acute Confusion in Elderly',
    ageConstraint: {
      min: 75
    }
  },
  {
    id: 'MTS-RULE-G003',
    name: 'Elderly with Reduced Mobility',
    description: 'Elderly patient (≥75 years) with new onset inability to mobilize or significant deterioration',
    category: 'symptom',
    condition: {
      parameter: 'additionalInfo',
      comparator: 'includes',
      value: 'reduced mobility',
    },
    impact: {
      urgency: 'standard', // Maps to 'Urgent' in MTS display
      description: 'Elderly patient with reduced mobility requires assessment',
      reference: 'MTS v3 Geriatric Considerations'
    },
    presentingComplaint: 'Mobility Problems',
    discriminator: 'Reduced Mobility in Elderly',
    ageConstraint: {
      min: 75
    }
  },
  {
    id: 'MTS-RULE-G004',
    name: 'Geriatric Fever',
    description: 'Elderly patient (≥75 years) with a temperature > 37.8°C',
    category: 'vital',
    condition: {
      parameter: 'vitals.temperature',
      comparator: 'gt',
      value: 37.8,
    },
    impact: {
      urgency: 'standard', // Maps to 'Urgent' in MTS display
      description: 'Elderly patient with low-grade fever requires assessment',
      reference: 'MTS v3 Geriatric Considerations'
    },
    presentingComplaint: 'Fever',
    discriminator: 'Geriatric Fever',
    ageConstraint: {
      min: 75
    }
  },
  {
    id: 'MTS-RULE-G005',
    name: 'Geriatric Hypotension',
    description: 'Elderly patient (≥75 years) with systolic BP < 110 mmHg',
    category: 'vital',
    condition: {
      parameter: 'vitals.bloodPressure',
      comparator: 'lt_systolic',
      value: 110,
    },
    impact: {
      urgency: 'urgent', // Maps to 'Very Urgent' in MTS display
      description: 'Elderly patient with hypotension requires rapid assessment',
      reference: 'MTS v3 Geriatric Considerations'
    },
    presentingComplaint: 'Dizziness',
    discriminator: 'Geriatric Hypotension',
    ageConstraint: {
      min: 75
    }
  }
];

// Description-based rules
export const DESCRIPTION_RULES: TriageRule[] = [
  {
    id: 'MTS-RULE-DESC-001',
    name: 'Cardiac Pain Description',
    description: 'Identifies cardiac chest pain from description',
    category: 'symptom',
    condition: {
      parameter: 'description',
      comparator: 'contains_any',
      value: [
        'crushing chest pain',
        'pressure on chest',
        'radiating to arm',
        'radiating to jaw',
        'chest heaviness'
      ]
    },
    impact: {
      urgency: 'urgent', // Example urgency, adjust as per MTS guidelines
      description: 'Description indicates possible cardiac chest pain',
      reference: 'MTS v3 Chest Pain flowchart'
    },
    presentingComplaint: 'Chest Pain',
    discriminator: 'Cardiac Pain'
  },
  {
    id: 'MTS-RULE-DESC-002',
    name: 'Severe Headache Description',
    description: 'Identifies concerning headache descriptions',
    category: 'symptom',
    condition: {
      parameter: 'description',
      comparator: 'contains_any',
      value: [
        'worst headache of life',
        'thunderclap headache',
        'sudden severe headache',
        'headache with neck stiffness'
      ]
    },
    impact: {
      urgency: 'urgent', // Example urgency, adjust as per MTS guidelines
      description: 'Description indicates possible serious headache',
      reference: 'MTS v3 Headache flowchart'
    },
    presentingComplaint: 'Headache',
    discriminator: 'Severe Pain'
  }
];

/**
 * Core Manchester Triage System rules
 * Each rule implements a specific discriminator for a specific presentation
 */
export const MTS_RULES: TriageRule[] = [
  // Include geriatric rules first for proper evaluation
  ...GERIATRIC_RULES,
  // Include description-based rules
  ...DESCRIPTION_RULES,
  // ===== IMMEDIATE (RED) CATEGORY RULES =====
  
  // Airway compromise rules
  {
    id: 'MTS-RULE-001',
    name: 'Compromised Airway',
    description: 'Patient shows signs of compromised airway requiring immediate intervention',
    category: 'vital',
    condition: {
      parameter: 'respiratory_distress',
      comparator: 'eq',
      value: true
    },
    impact: {
      urgency: 'immediate',
      description: 'Airway compromise requires immediate medical intervention',
      reference: 'MTS v3.0, Sec 2.1.1'
    },
    presentingComplaint: 'Shortness of Breath in Adults',
    discriminator: 'Airway Compromise'
  },
  {
    id: 'MTS-RULE-002',
    name: 'Stridor',
    description: 'Patient presenting with audible stridor, indicating upper airway obstruction',
    category: 'vital',
    condition: {
      parameter: 'additionalInfo',
      comparator: 'includes',
      value: 'stridor'
    },
    impact: {
      urgency: 'immediate',
      description: 'Stridor indicates upper airway obstruction requiring immediate assessment',
      reference: 'MTS v3.0, Sec 2.1.3'
    },
    presentingComplaint: 'Shortness of Breath in Adults',
    discriminator: 'Stridor'
  },
  
  // Breathing problems rules
  {
    id: 'MTS-RULE-003',
    name: 'Inadequate Breathing',
    description: 'Patient with severe respiratory distress unable to maintain adequate ventilation',
    category: 'vital',
    condition: {
      parameter: 'vitals.respiratoryRate',
      comparator: 'gt',
      value: '30'
    },
    impact: {
      urgency: 'immediate',
      description: 'Severe tachypnea indicates respiratory failure requiring immediate intervention',
      reference: 'MTS v3.0, Sec 2.1.4'
    },
    presentingComplaint: 'Shortness of Breath in Adults',
    discriminator: 'Inadequate Breathing'
  },
  {
    id: 'MTS-RULE-004',
    name: 'Respiratory Exhaustion - Adults',
    description: 'Adult patient showing signs of respiratory muscle fatigue or exhaustion',
    category: 'vital',
    condition: {
      parameter: 'additionalInfo',
      comparator: 'includes',
      value: 'respiratory exhaustion'
    },
    impact: {
      urgency: 'immediate',
      description: 'Respiratory muscle fatigue may rapidly progress to respiratory arrest',
      reference: 'MTS v3.0, Sec 2.1.5'
    },
    presentingComplaint: 'Shortness of Breath in Adults',
    discriminator: 'Respiratory Exhaustion'
  },
  
  // Circulatory problems rules
  {
    id: 'MTS-RULE-005',
    name: 'Shock',
    description: 'Patient showing signs of shock with inadequate tissue perfusion',
    category: 'vital',
    condition: {
      parameter: 'vitals.bloodPressure',
      comparator: 'includes',
      value: 'shock'
    },
    impact: {
      urgency: 'immediate',
      description: 'Shock requires immediate fluid resuscitation and intervention',
      reference: 'MTS v3.0, Sec 2.2.1'
    },
    presentingComplaint: 'Collapsed Adult',
    discriminator: 'Shock'
  },
  {
    id: 'MTS-RULE-006',
    name: 'Exsanguinating Hemorrhage',
    description: 'Patient with massive uncontrolled bleeding',
    category: 'vital',
    condition: {
      parameter: 'bleeding.severity',
      comparator: 'eq',
      value: 'severe'
    },
    impact: {
      urgency: 'immediate',
      description: 'Massive bleeding requires immediate hemorrhage control and resuscitation',
      reference: 'MTS v3.0, Sec 2.2.2'
    },
    presentingComplaint: 'Major Trauma',
    discriminator: 'Exsanguinating Hemorrhage'
  },
  
  // Neurological rules
  {
    id: 'MTS-RULE-007',
    name: 'Unresponsive Patient',
    description: 'Patient with GCS ≤ 8 or unresponsive to pain (AVPU = U)',
    category: 'vital',
    condition: {
      parameter: 'vitals.avpu',
      comparator: 'eq',
      value: 'unresponsive'
    },
    impact: {
      urgency: 'immediate',
      description: 'Unresponsive patient requires immediate airway management and neurological assessment',
      reference: 'MTS v3.0, Sec 2.3.1'
    },
    presentingComplaint: 'Collapsed Adult',
    discriminator: 'Unresponsive'
  },
  {
    id: 'MTS-RULE-008',
    name: 'Ongoing Seizure',
    description: 'Patient actively seizing upon presentation',
    category: 'vital',
    condition: {
      parameter: 'additionalInfo',
      comparator: 'includes',
      value: 'actively seizing'
    },
    impact: {
      urgency: 'immediate',
      description: 'Ongoing seizure may progress to status epilepticus requiring immediate intervention',
      reference: 'MTS v3.0, Sec 2.3.2'
    },
    presentingComplaint: 'Fits',
    discriminator: 'Ongoing Seizure'
  },
  
  // Mental health rules
  {
    id: 'MTS-RULE-080',
    name: 'Actively Suicidal',
    description: 'Patient with active suicidal ideation or attempt',
    category: 'symptom',
    condition: {
      parameter: 'additionalInfo',
      comparator: 'contains_any',
      value: ['suicide attempt', 'suicidal', 'trying to kill', 'end life', 'wants to die']
    },
    impact: {
      urgency: 'immediate',
      description: 'Patient with active suicidal ideation requires immediate intervention',
      reference: 'MTS v3.0, Sec 4.1.1'
    },
    presentingComplaint: 'Mental Health Problem',
    discriminator: 'Actively Suicidal'
  },
  {
    id: 'MTS-RULE-081',
    name: 'Acute Psychosis',
    description: 'Patient with acute psychotic symptoms including delusions, hallucinations, or disorganized thinking',
    category: 'symptom',
    condition: {
      parameter: 'symptoms',
      comparator: 'contains_any',
      value: ['hallucinations', 'delusions', 'psychosis', 'paranoia']
    },
    impact: {
      urgency: 'urgent',
      description: 'Patient with acute psychosis requires prompt psychiatric assessment',
      reference: 'MTS v3.0, Sec 4.1.2'
    },
    presentingComplaint: 'Mental Health Problem',
    discriminator: 'Acute Psychosis'
  },
  {
    id: 'MTS-RULE-081-ADDITIONAL',
    name: 'Acute Psychosis - Additional Info',
    description: 'Detection of psychotic symptoms in patient additional information',
    category: 'symptom',
    condition: {
      parameter: 'additionalInfo',
      comparator: 'contains_any',
      value: ['delusion', 'hallucination', 'psychosis', 'psychotic', 'disorganized thinking']
    },
    impact: {
      urgency: 'urgent',
      description: 'Patient with acute psychosis requires prompt psychiatric assessment',
      reference: 'MTS v3.0, Sec 4.1.2'
    },
    presentingComplaint: 'Mental Health Problem',
    discriminator: 'Acute Psychosis'
  },
  {
    id: 'MTS-RULE-081-CHIEF',
    name: 'Acute Psychosis - Chief Complaint',
    description: 'Mental health presentation with acute psychotic features',
    category: 'compound',
    condition: [
      {
        parameter: 'chiefComplaint',
        comparator: 'eq',
        value: 'Mental Health Problem'
      },
      {
        parameter: 'symptoms',
        comparator: 'contains_any',
        value: ['hallucinations', 'delusions', 'psychosis', 'paranoia', 'altered mental status', 'confusion']
      }
    ],
    impact: {
      urgency: 'urgent',
      description: 'Mental health problems with psychotic features require urgent psychiatric assessment',
      reference: 'MTS v3.0, Sec 4.1.2'
    },
    presentingComplaint: 'Mental Health Problem',
    discriminator: 'Acute Psychosis'
  },
  
  {
    id: 'MTS-RULE-030',
    name: 'Severe Neurological Compromise (GCS <= 8)',
    description: 'Patient with Glasgow Coma Scale score of 8 or less, indicating severe neurological compromise.',
    category: 'vital',
    condition: {
      parameter: 'vitals.gcs.total',
      comparator: 'lte',
      value: 8
    },
    impact: {
      urgency: 'immediate',
      description: 'GCS <= 8 requires immediate airway management and comprehensive neurological assessment.',
      reference: 'MTS v3.0, Sec 2.3.x (GCS Adaptation)'
    },
    presentingComplaint: 'Altered Consciousness',
    discriminator: 'GCS <= 8'
  },
  // Pediatric immediate rules
  {
    id: 'MTS-RULE-009',
    name: 'Unresponsive Child',
    description: 'Child unresponsive to pain (AVPU = U)',
    category: 'vital',
    condition: {
      parameter: 'vitals.avpu',
      comparator: 'eq',
      value: 'unresponsive'
    },
    impact: {
      urgency: 'immediate',
      description: 'Unresponsive child requires immediate airway management and assessment',
      reference: 'MTS v3.0, Sec 2.4.1'
    },
    presentingComplaint: 'Collapsed Child',
    discriminator: 'Unresponsive'
  },
  {
    id: 'MTS-RULE-010',
    name: 'Stridor in Child',
    description: 'Child presenting with audible stridor',
    category: 'vital',
    condition: {
      parameter: 'additionalInfo',
      comparator: 'includes',
      value: 'stridor'
    },
    impact: {
      urgency: 'immediate',
      description: 'Stridor in children can rapidly progress to complete airway obstruction',
      reference: 'MTS v3.0, Sec 2.4.3'
    },
    presentingComplaint: 'Shortness of Breath in Children',
    discriminator: 'Stridor',
    ageConstraint: { max: 15 } // Apply to children under 16
  },
  
  // ===== VERY URGENT (ORANGE) CATEGORY RULES =====
  
  // Respiratory very urgent rules
  {
    id: 'MTS-RULE-011',
    name: 'Acute Shortness of Breath',
    description: 'Patient unable to complete sentences due to breathlessness',
    category: 'symptom',
    condition: {
      parameter: 'additionalInfo',
      comparator: 'includes',
      value: 'cannot speak in sentences'
    },
    impact: {
      urgency: 'urgent', // This corresponds to 'Very Urgent' in the MTS display terminology
      description: 'Severe breathlessness requires rapid intervention to prevent deterioration',
      reference: 'MTS v3.0, Sec 3.1.1'
    },
    presentingComplaint: 'Shortness of Breath in Adults',
    discriminator: 'Inability to Talk in Sentences'
  },
  {
    id: 'MTS-RULE-033',
    name: 'Severe Hypothermia',
    description: 'Patient exhibits signs of severe hypothermia with core body temperature below 28°C.',
    category: 'vital',
    condition: {
      parameter: 'vitals.temperature',
      comparator: 'lt',
      value: '28'
    },
    impact: {
      urgency: 'immediate',
      description: 'Core temperature <28°C is life-threatening. High risk of cardiac arrest.',
      reference: 'MTS General Guidelines / Hypothermia Management'
    },
    presentingComplaint: 'General Weakness / Collapse / Exposure / Altered Consciousness',
    discriminator: 'Severe Hypothermia (<28°C)'
    // No ageConstraint means applies to all ages
  },
  {
    id: 'MTS-RULE-012',
    name: 'Audible Wheeze',
    description: 'Patient with audible wheeze without stethoscope',
    category: 'symptom',
    condition: {
      parameter: 'additionalInfo',
      comparator: 'includes',
      value: 'audible wheeze'
    },
    impact: {
      urgency: 'urgent',
      description: 'Audible wheeze indicates significant bronchospasm requiring prompt treatment',
      reference: 'MTS v3.0, Sec 3.1.2'
    },
    presentingComplaint: 'Asthma',
    discriminator: 'Audible Wheeze'
  },
  
  // Cardiovascular very urgent rules
  {
    id: 'MTS-RULE-013',
    name: 'Cardiac Chest Pain',
    description: 'Patient with chest pain suspicious for cardiac origin',
    category: 'symptom',
    condition: {
      parameter: 'symptoms',
      comparator: 'includes',
      value: 'chest pain'
    },
    impact: {
      urgency: 'urgent',
      description: 'Potential cardiac chest pain requires rapid assessment to rule out acute coronary syndrome',
      reference: 'MTS v3.0, Sec 3.2.1'
    },
    presentingComplaint: 'Chest Pain',
    discriminator: 'Chest Pain'
  },
  {
    id: 'MTS-RULE-014',
    name: 'Significant Tachycardia',
    description: 'Adult with heart rate > 120 bpm',
    category: 'vital',
    condition: {
      parameter: 'vitals.heartRate',
      comparator: 'gt',
      value: '120'
    },
    impact: {
      urgency: 'urgent',
      description: 'Significant tachycardia may indicate serious underlying condition requiring prompt assessment',
      reference: 'MTS v3.0, Sec 3.2.2'
    },
    presentingComplaint: 'Palpitations',
    discriminator: 'Tachycardia'
  },
  
  // Neurological very urgent rules
  {
    id: 'MTS-RULE-015',
    name: 'Altered Consciousness',
    description: 'Patient with GCS 9-12 or only responding to voice (AVPU = V)',
    category: 'vital',
    condition: {
      parameter: 'vitals.avpu',
      comparator: 'eq',
      value: 'verbal'
    },
    impact: {
      urgency: 'urgent',
      description: 'Altered consciousness requires rapid neurological assessment',
      reference: 'MTS v3.0, Sec 3.3.1'
    },
    presentingComplaint: 'Collapsed Adult',
    discriminator: 'Altered Level of Consciousness'
  },
  {
    id: 'MTS-RULE-016',
    name: 'Acute Neurological Deficit',
    description: 'Patient with new onset focal neurological symptoms',
    category: 'symptom',
    condition: {
      parameter: 'additionalInfo',
      comparator: 'includes',
      value: 'neurological deficit'
    },
    impact: {
      urgency: 'urgent',
      description: 'Acute neurological deficit may indicate stroke requiring time-critical intervention',
      reference: 'MTS v3.0, Sec 3.3.2'
    },
    presentingComplaint: 'Headache',
    discriminator: 'Acute Neurological Deficit'
  },
  
  {
    id: 'MTS-RULE-031',
    name: 'Moderate Neurological Compromise (GCS 9-12)',
    description: 'Patient with Glasgow Coma Scale score between 9 and 12, indicating moderate neurological compromise.',
    category: 'vital',
    condition: {
      parameter: 'vitals.gcs.total',
      comparator: 'lte',
      value: 12
    },
    impact: {
      urgency: 'urgent',
      description: 'GCS 9-12 requires rapid neurological assessment and monitoring.',
      reference: 'MTS v3.0, Sec 3.3.x (GCS Adaptation)'
    },
    presentingComplaint: 'Altered Consciousness',
    discriminator: 'GCS 9-12'
  },
  // Pain-related very urgent rules
  {
    id: 'MTS-RULE-017',
    name: 'Severe Pain',
    description: 'Patient reporting pain score 8-10/10',
    category: 'symptom',
    condition: {
      parameter: 'vitals.painScore',
      comparator: 'gte',
      value: '8'
    },
    impact: {
      urgency: 'urgent',
      description: 'Severe pain requires rapid analgesia and assessment of underlying cause',
      reference: 'MTS v3.0, Sec 3.4.1'
    },
    presentingComplaint: 'Abdominal Pain in Adults',
    discriminator: 'Severe Pain'
  },
  
  // Hemorrhage very urgent rules
  {
    id: 'MTS-RULE-018',
    name: 'Fresh Blood Per Rectum',
    description: 'Patient passing fresh red blood from rectum',
    category: 'symptom',
    condition: {
      parameter: 'additionalInfo',
      comparator: 'includes',
      value: 'fresh rectal bleeding'
    },
    impact: {
      urgency: 'urgent',
      description: 'Fresh rectal bleeding may indicate significant gastrointestinal hemorrhage',
      reference: 'MTS v3.0, Sec 3.5.1'
    },
    presentingComplaint: 'Gastrointestinal Bleeding',
    discriminator: 'Fresh Blood Per Rectum'
  },
  
  // Pediatric very urgent rules
  {
    id: 'MTS-RULE-019',
    name: 'Very Hot Child',
    description: 'Child with temperature > 39°C',
    category: 'vital',
    condition: {
      parameter: 'vitals.temperature',
      comparator: 'gt',
      value: '39'
    },
    impact: {
      urgency: 'urgent',
      description: 'High fever in children requires prompt assessment for serious infection',
      reference: 'MTS v3.0, Sec 3.6.1'
    },
    presentingComplaint: 'Fever',
    discriminator: 'Very Hot'
  },
  {
    id: 'MTS-RULE-020',
    name: 'Inconsolable Child',
    description: 'Child remains distressed despite parental comfort',
    category: 'symptom',
    condition: {
      parameter: 'additionalInfo',
      comparator: 'includes',
      value: 'inconsolable'
    },
    impact: {
      urgency: 'urgent',
      description: 'Inconsolable child may indicate serious underlying condition',
      reference: 'MTS v3.0, Sec 3.6.2'
    },
    presentingComplaint: 'Crying Baby',
    discriminator: 'Inconsolable by Parents',
    ageConstraint: {
      min: 0,
      max: 16 // Standard pediatric age limit
    }
  },
  {
    id: 'MTS-RULE-034A',
    name: 'Moderate Hypothermia - Adult',
    description: 'Adult patient (>=16 years) with core body temperature below 32°C (triggering if not already caught by Severe Hypothermia).',
    category: 'vital',
    condition: {
      parameter: 'vitals.temperature',
      comparator: 'lt', 
      value: '32' 
    },
    impact: {
      urgency: 'urgent', // This corresponds to 'Very Urgent' in the MTS display terminology
      description: 'Moderate hypothermia (28-31.9°C) in an adult. Risk of arrhythmias, CNS depression.',
      reference: 'MTS General Guidelines / Hypothermia Management'
    },
    presentingComplaint: 'Exposure / Confusion / Shivering / Slow Response',
    discriminator: 'Moderate Hypothermia (Adult)',
    ageConstraint: { min: 16 }
  },
  {
    id: 'MTS-RULE-034C',
    name: 'Moderate Hypothermia - Child',
    description: 'Child patient (<16 years) with core body temperature below 32°C (triggering if not already caught by Severe Hypothermia).',
    category: 'vital',
    condition: {
      parameter: 'vitals.temperature',
      comparator: 'lt',
      value: '32'
    },
    impact: {
      urgency: 'urgent', // This corresponds to 'Very Urgent' in the MTS display terminology
      description: 'Moderate hypothermia (28-31.9°C) in a child. Children are more vulnerable.',
      reference: 'MTS Pediatric Guidelines / Hypothermia Management'
    },
    presentingComplaint: 'Exposure / Lethargy / Pale / Cool Skin',
    discriminator: 'Moderate Hypothermia (Child)',
    ageConstraint: { max: 16 }
  },
  
  // ===== URGENT (YELLOW) CATEGORY RULES =====
  
  // Pain-related urgent rules
  {
    id: 'MTS-RULE-021',
    name: 'Moderate Pain',
    description: 'Patient reporting pain score 5-7/10',
    category: 'symptom',
    condition: {
      parameter: 'vitals.painScore',
      comparator: 'gte',
      value: '5'
    },
    impact: {
      urgency: 'standard',
      description: 'Moderate pain requires assessment and appropriate analgesia',
      reference: 'MTS v3.0, Sec 4.1.1'
    },
    presentingComplaint: 'Abdominal Pain in Adults',
    discriminator: 'Moderate Pain'
  },
  
  {
    id: 'MTS-RULE-032',
    name: 'Mild Neurological Compromise (GCS 13-14)',
    description: 'Patient with Glasgow Coma Scale score between 13 and 14, indicating mild neurological compromise.',
    category: 'vital',
    condition: {
      parameter: 'vitals.gcs.total',
      comparator: 'lte',
      value: 14
    },
    impact: {
      urgency: 'standard',
      description: 'GCS 13-14 requires neurological assessment and observation.',
      reference: 'MTS v3.0, Sec 4.x.x (GCS Adaptation)'
    },
    presentingComplaint: 'Altered Consciousness',
    discriminator: 'GCS 13-14'
  },
  // Temperature-related urgent rules
  {
    id: 'MTS-RULE-022',
    name: 'Hot Adult',
    description: 'Adult with temperature 38-38.5°C',
    category: 'vital',
    condition: {
      parameter: 'vitals.temperature',
      comparator: 'gte',
      value: '38'
    },
    impact: {
      urgency: 'standard',
      description: 'Fever requires assessment for source of infection',
      reference: 'MTS v3.0, Sec 4.2.1'
    },
    presentingComplaint: 'Fever',
    discriminator: 'Hot'
  },
  
  // Vomiting-related urgent rules
  {
    id: 'MTS-RULE-023',
    name: 'Persistent Vomiting',
    description: 'Patient with continuous or frequently recurring vomiting',
    category: 'symptom',
    condition: {
      parameter: 'additionalInfo',
      comparator: 'includes',
      value: 'persistent vomiting'
    },
    impact: {
      urgency: 'standard',
      description: 'Persistent vomiting may lead to dehydration and requires assessment',
      reference: 'MTS v3.0, Sec 4.3.1'
    },
    presentingComplaint: 'Vomiting',
    discriminator: 'Vomiting'
  },
  
  // Trauma-related urgent rules
  {
    id: 'MTS-RULE-024',
    name: 'Significant Mechanism of Injury',
    description: 'Patient injured through significant force or energy transfer',
    category: 'mechanism',
    condition: {
      parameter: 'mechanism',
      comparator: 'includes',
      value: 'high energy'
    },
    impact: {
      urgency: 'standard',
      description: 'Significant mechanism may result in occult injuries requiring assessment',
      reference: 'MTS v3.0, Sec 4.4.1'
    },
    presentingComplaint: 'Major Trauma',
    discriminator: 'High-Energy Transfer'
  },
  
  // Pediatric urgent rules
  {
    id: 'MTS-RULE-025',
    name: 'Hot Child',
    description: 'Child with temperature 38-39°C',
    category: 'vital',
    condition: {
      parameter: 'vitals.temperature',
      comparator: 'gte',
      value: '38'
    },
    impact: {
      urgency: 'standard',
      description: 'Fever in children requires assessment for source of infection',
      reference: 'MTS v3.0, Sec 4.5.1'
    },
    presentingComplaint: 'Unwell Child',
    discriminator: 'Hot'
  },
  
  // ===== STANDARD (GREEN) CATEGORY RULES =====
  
  // Recent problem rules
  {
    id: 'MTS-RULE-026',
    name: 'Recent Problem',
    description: 'Symptoms with onset within the last week',
    category: 'symptom',
    condition: {
      parameter: 'duration',
      comparator: 'includes',
      value: 'days'
    },
    impact: {
      urgency: 'non-urgent',
      description: 'Recent onset problems requiring routine assessment',
      reference: 'MTS v3.0, Sec 5.1.1'
    },
    presentingComplaint: 'Abdominal Pain in Adults',
    discriminator: 'Recent Problem'
  },
  
  // Low pain rules
  {
    id: 'MTS-RULE-027',
    name: 'Low Pain',
    description: 'Patient reporting pain score 1-4/10',
    category: 'symptom',
    condition: {
      parameter: 'vitals.painScore',
      comparator: 'lte',
      value: '4'
    },
    impact: {
      urgency: 'non-urgent',
      description: 'Mild pain suitable for routine assessment',
      reference: 'MTS v3.0, Sec 5.2.1'
    },
    presentingComplaint: 'Abdominal Pain in Adults',
    discriminator: 'Low Pain'
  },
  {
    id: 'MTS-RULE-035A',
    name: 'Mild Hypothermia - Adult',
    description: 'Adult patient (>=16 years) with core body temperature below 35°C (triggering if not already caught by more severe hypothermia rules).',
    category: 'vital',
    condition: {
      parameter: 'vitals.temperature',
      comparator: 'lt',
      value: '35'
    },
    impact: {
      urgency: 'urgent',
      description: 'Mild hypothermia (32-34.9°C) in an adult. Requires warming and monitoring.',
      reference: 'MTS General Guidelines / Hypothermia Management'
    },
    presentingComplaint: 'Exposure / Shivering / Cold to Touch',
    discriminator: 'Mild Hypothermia (Adult)',
    ageConstraint: { min: 16 }
  },
  {
    id: 'MTS-RULE-035C',
    name: 'Mild Hypothermia - Child',
    description: 'Child patient (<16 years) with core body temperature below 35°C (triggering if not already caught by more severe hypothermia rules).',
    category: 'vital',
    condition: {
      parameter: 'vitals.temperature',
      comparator: 'lt',
      value: '35'
    },
    impact: {
      urgency: 'urgent',
      description: 'Mild hypothermia (32-34.9°C) in a child. Requires warming, monitor for deterioration.',
      reference: 'MTS Pediatric Guidelines / Hypothermia Management'
    },
    presentingComplaint: 'Exposure / Shivering / Irritable / Quiet',
    discriminator: 'Mild Hypothermia (Child)',
    ageConstraint: { max: 16 }
  }
];

/**
 * Helper function to find a rule by ID
 */
export function findRuleById(id: string): TriageRule | undefined {
  return MTS_RULES.find(rule => rule.id === id);
}

/**
 * Helper function to filter rules by presenting complaint
 */
export function getRulesByPresentation(presentingComplaint: string): TriageRule[] {
  return MTS_RULES.filter(
    rule => rule.presentingComplaint === presentingComplaint
  );
}

/**
 * Helper function to filter rules by discriminator
 */
export function getRulesByDiscriminator(discriminator: string): TriageRule[] {
  return MTS_RULES.filter(
    rule => rule.discriminator === discriminator
  );
}

/**
 * Helper function to filter rules by urgency level
 */
export function getRulesByUrgency(urgency: string): TriageRule[] {
  return MTS_RULES.filter(
    rule => rule.impact.urgency === urgency
  );
}

/**
 * Evaluate a rule against patient data
 */
export function evaluateRule(rule: TriageRule, triageData: TriageData): boolean {
  // Check age constraint first if it exists
  if (rule.ageConstraint) {
    const patientAge = triageData.age;
    if (patientAge === undefined) {
      // If age is unknown and rule has age constraint, it cannot be safely applied
      console.log(`[Rules Debug] Rule ${rule.id} (${rule.name}) skipped: age unknown, but rule has age constraint.`);
      return false;
    }
    const { min, max } = rule.ageConstraint;
    if ((min !== undefined && patientAge < min) || (max !== undefined && patientAge > max)) {
      console.log(`[Rules Debug] Rule ${rule.id} (${rule.name}) skipped: patient age ${patientAge} outside constraint [${min === undefined ? '-inf' : min}-${max === undefined ? 'inf' : max}].`);
      return false;
    }
  }

  // Debug log the rule being evaluated
  console.log(`[Rules Debug] Evaluating rule ${rule.id} - ${rule.name}`);
  
  // Handle compound rules with multiple conditions (AND logic)
  if (rule.category === 'compound' && Array.isArray(rule.condition)) {
    console.log(`[Rules Debug] Evaluating compound rule with ${rule.condition.length} conditions`);
    
    // All conditions must be true for a compound rule to match
    for (const singleCondition of rule.condition) {
      const { parameter, comparator, value } = singleCondition;
      console.log(`[Rules Debug] Compound condition: ${parameter} ${comparator} ${value}`);
      
      const conditionResult = evaluateSingleCondition(rule.id, rule.name, parameter, comparator, value, triageData);
      
      if (!conditionResult) {
        console.log(`[Rules Debug] Compound rule ${rule.id} (${rule.name}) failed at condition: ${parameter} ${comparator} ${value}`);
        return false; // If any condition fails, the whole rule fails
      }
    }
    
    console.log(`[Rules Debug] Compound rule ${rule.id} (${rule.name}) matched - all conditions passed`);
    return true; // All conditions passed
  }
  
  // Handle regular rules with a single condition
  if (!Array.isArray(rule.condition)) {
    const { parameter, comparator, value } = rule.condition;
    console.log(`[Rules Debug] Condition: ${parameter} ${comparator} ${value}`);
    return evaluateSingleCondition(rule.id, rule.name, parameter, comparator, value, triageData);
  }
  
  // This should not happen if the rule is properly defined
  console.warn(`[Rules Debug] Rule ${rule.id} (${rule.name}) has invalid condition structure`);
  return false;
}

/**
 * Helper function to evaluate a single condition
 */
function evaluateSingleCondition(
  ruleId: string,
  ruleName: string,
  parameter: string,
  comparator: Comparator,
  value: string | number | boolean | string[],
  triageData: TriageData
): boolean {
  // Handle nested parameters (e.g., vitals.temperature, vitals.gcs.total)
  if (parameter.includes('.')) {
    const parts = parameter.split('.');
    let resolvedValue: any = triageData;

    // Special handling for GCS total calculation
    const isGCSTotal = parameter === 'vitals.gcs.total';

    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];
      const isLastPart = i === parts.length - 1;
      
      if (resolvedValue && typeof resolvedValue === 'object' && part in resolvedValue) {
        resolvedValue = resolvedValue[part];
      } else {
        // Special case: If we're looking for vitals.gcs.total and we're at the 'total' part,
        // try to calculate it from the components
        if (isGCSTotal && isLastPart && part === 'total' && 
            resolvedValue && typeof resolvedValue === 'object' &&
            'eye' in resolvedValue && typeof resolvedValue.eye === 'number' &&
            'verbal' in resolvedValue && typeof resolvedValue.verbal === 'number' &&
            'motor' in resolvedValue && typeof resolvedValue.motor === 'number') {
          
          resolvedValue = calculateGCSTotal(resolvedValue as { eye: number; verbal: number; motor: number });
          console.log(`[Rules Debug] Calculated GCS total for rule ${ruleId} (${ruleName}): ${resolvedValue}`);
        } else {
          console.log(`[Rules Debug] Rule ${ruleId} (${ruleName}) failed: path '${parameter}' invalid at '${part}'. Current object state before failure:`, resolvedValue, "Full Triage Data:", JSON.stringify(triageData));
          return false; // Path is invalid
        }
      }
    }

    if (resolvedValue === undefined) {
      console.log(`[Rules Debug] Rule ${ruleId} (${ruleName}) failed: parameter '${parameter}' resolved to undefined after path traversal and any calculations.`);
      return false;
    }

    const result = compareValues(parameter, resolvedValue, comparator, value);
    console.log(`[Rules Debug] Rule ${ruleId} (${ruleName}) condition result: ${result} (Value: ${resolvedValue}, Comparator: ${comparator}, Expected: ${value})`);
    return result;
  }
  
  // Handle direct parameters
  const paramValue = triageData[parameter as keyof TriageData];
  if (paramValue === undefined) {
    console.log(`[Rules Debug] Rule ${ruleId} failed: parameter '${parameter}' is undefined in triageData`);
    return false;
  }
  
  const result = compareValues(parameter, paramValue, comparator, value);
  console.log(`[Rules Debug] Rule ${ruleId} condition result: ${result} (${paramValue} ${comparator} ${value})`);
  return result;
}

/**
 * Calculates the total Glasgow Coma Scale (GCS) score from its components.
 * @param gcsComponents An object containing eye, verbal, and motor scores.
 * @returns The total GCS score.
 */
export function calculateGCSTotal(gcsComponents: { eye: number; verbal: number; motor: number }): number {
  // Basic validation for individual component ranges could be added here for robustness
  // e.g., eye (1-4), verbal (1-5), motor (1-6).
  // For now, assuming valid components are passed.
  return gcsComponents.eye + gcsComponents.verbal + gcsComponents.motor;
}

/**
 * Normalize temperature to Celsius for consistent evaluation
 * Normal body temperature is ~37°C or ~98.6°F
 */
export function normalizeTemperature(temp: number): number {
  // If temperature is likely in Fahrenheit (normal body temp is ~98.6°F)
  if (temp > 45) {
    // Convert Fahrenheit to Celsius: (F - 32) * 5/9
    const celsius = (temp - 32) * 5/9;
    console.log(`[Rules Debug] Converting temperature from ${temp}°F to ${celsius.toFixed(1)}°C`);
    return celsius;
  }
  // Already in Celsius
  console.log(`[Rules Debug] Temperature ${temp} assumed to be in Celsius`);
  return temp;
}

/**
 * Compare values based on the specified comparator
 */
function compareValues(parameterName: string, actual: any, comparator: Comparator, expected: any): boolean {
  // Handle null/undefined values at the very start
  if (actual === undefined || actual === null) {
    console.log(`[Rules Debug] Parameter ${parameterName} ('${actual}') is undefined or null. Rule evaluation will be false.`);
    return false;
  }
  // Handle boolean values specifically
  if (typeof expected === 'boolean' || expected === 'true' || expected === 'false') {
    const actualBool = typeof actual === 'string' ? actual.toLowerCase() === 'true' : !!actual;
    const expectedBool = typeof expected === 'string' ? expected.toLowerCase() === 'true' : !!expected;
    console.log(`[Rules Debug] Comparing booleans: ${actualBool} ${comparator} ${expectedBool}`);
    if (comparator === 'eq') return actualBool === expectedBool;
    if (comparator === 'neq') return actualBool !== expectedBool;
    return false; // Other comparators don't make sense for booleans
  }

  // Handle blood pressure systolic comparisons
  if (parameterName === 'vitals.bloodPressure' && 
      ['lt_systolic', 'gt_systolic', 'lte_systolic', 'gte_systolic'].includes(comparator)) {
    if (typeof actual !== 'string' || !actual.includes('/')) {
      console.warn(`[Rules Debug] Invalid blood pressure format for ${parameterName}: ${actual}. Expected 'systolic/diastolic'. Rule evaluation will be false.`);
      return false;
    }
    const parts = actual.split('/');
    if (parts.length < 2) {
        console.warn(`[Rules Debug] Incomplete blood pressure format for ${parameterName}: ${actual}. Expected 'systolic/diastolic'. Rule evaluation will be false.`);
        return false;
    }
    const actualSystolic = parseInt(parts[0], 10);
    const expectedValueNumeric = Number(expected);

    if (isNaN(actualSystolic) || isNaN(expectedValueNumeric)) {
      console.warn(`[Rules Debug] Non-numeric value in systolic BP comparison: actualSystolic=${actualSystolic} (from '${actual}'), expectedValue=${expectedValueNumeric} (from '${expected}'). Rule evaluation will be false.`);
      return false;
    }
    
    const baseComparator = comparator.replace('_systolic', '');
    console.log(`[Rules Debug] Comparing systolic BP for '${parameterName}': Actual Systolic ${actualSystolic} ${baseComparator} Expected Threshold ${expectedValueNumeric} (Raw Actual: '${actual}', Comparator: '${comparator}')`);

    switch (comparator) {
      case 'lt_systolic': return actualSystolic < expectedValueNumeric;
      case 'gt_systolic': return actualSystolic > expectedValueNumeric;
      case 'lte_systolic': return actualSystolic <= expectedValueNumeric;
      case 'gte_systolic': return actualSystolic >= expectedValueNumeric;
      default: 
        console.warn(`[Rules Debug] Unhandled systolic comparator: ${comparator} in BP logic.`);
        return false; 
    }
  }
  
  // Handle text-specific comparators
  if (typeof actual === 'string') {
    if (comparator === 'contains' && typeof expected === 'string') {
      const result = containsPhrase(actual, expected);
      console.log(`[Rules Debug] Text 'contains' check for '${parameterName}': Actual '${actual}' contains Expected '${expected}' = ${result}`);
      return result;
    }

    if (comparator === 'contains_any' && Array.isArray(expected) && expected.every(e => typeof e === 'string')) {
      const result = containsAnyPhrase(actual, expected as string[]);
      console.log(`[Rules Debug] Text 'contains_any' check for '${parameterName}': Actual '${actual}' contains any of [${expected.join(', ')}] = ${result}`);
      return result;
    }

    if (comparator === 'contains_all' && Array.isArray(expected) && expected.every(e => typeof e === 'string')) {
      const result = containsAllPhrases(actual, expected as string[]);
      console.log(`[Rules Debug] Text 'contains_all' check for '${parameterName}': Actual '${actual}' contains all of [${expected.join(', ')}] = ${result}`);
      return result;
    }
  }
  
  // Convert string numbers to actual numbers for generic numeric comparisons (gt, gte, lt, lte)
  // This block will not be entered if a systolic comparator was used and handled above.
  if (!isNaN(Number(actual)) && !isNaN(Number(expected)) && 
      ['gt', 'gte', 'lt', 'lte'].includes(comparator)) {
    actual = Number(actual);
    expected = Number(expected);
    
    // Special handling for temperature comparisons
    if ((parameterName === 'vitals.temperature' || parameterName === 'temperature') && (actual > 45 || (expected > 45 && ['lt', 'lte'].includes(comparator)))) {
      if (actual > 45) actual = normalizeTemperature(actual);
      // Note: existing logic for normalizing 'expected' temperature is kept, though its typical use case might be for Celsius thresholds.
      if (expected > 45) expected = normalizeTemperature(expected);
      console.log(`[Rules Debug] After temperature normalization: ${actual} ${comparator} ${expected}`);
    }
  }
  
  switch (comparator) {
    case 'eq': 
      return actual === expected;
    case 'neq': 
      return actual !== expected;
    case 'gt': 
      return actual > expected;
    case 'gte': 
      return actual >= expected;
    case 'lt': 
      return actual < expected;
    case 'lte': 
      return actual <= expected;
    case 'includes':
      if (typeof actual === 'string') {
        const result = actual.toLowerCase().includes(String(expected).toLowerCase());
        console.log(`[Rules Debug] String includes check: '${actual}' includes '${expected}' = ${result}`);
        return result;
      }
      if (Array.isArray(actual)) {
        const expectedLower = String(expected).toLowerCase();
        const directMatch = actual.some(item => 
          String(item).toLowerCase().includes(expectedLower)
        );
        const reverseMatch = actual.some(item => 
          expectedLower.includes(String(item).toLowerCase())
        );
        const result = directMatch || reverseMatch;
        console.log(`[Rules Debug] Array includes check: [${actual.join(', ')}] includes/matches '${expected}' = ${result}`);
        return result;
      }
      return false;
    case 'excludes':
      if (typeof actual === 'string') {
        return !actual.toLowerCase().includes(String(expected).toLowerCase());
      }
      if (Array.isArray(actual)) {
        return !actual.some(item => 
          String(item).toLowerCase().includes(String(expected).toLowerCase())
        );
      }
      return true;
    case 'matches':
      if (typeof actual === 'string') {
        return new RegExp(String(expected), 'i').test(actual);
      }
      return false;
    case 'contains_any':
      if (typeof actual === 'string') {
        // This case is already handled in the text-specific comparators section above
        // But we'll include it here for completeness
        if (typeof expected === 'string') {
          const expectedArray = expected.split(',').map(item => item.trim());
          const result = containsAnyPhrase(actual, expectedArray);
          console.log(`[Rules Debug] String contains_any check: '${actual}' contains any of [${expectedArray.join(', ')}] = ${result}`);
          return result;
        }
        if (Array.isArray(expected)) {
          const result = containsAnyPhrase(actual, expected as string[]);
          console.log(`[Rules Debug] String contains_any check: '${actual}' contains any of [${expected.join(', ')}] = ${result}`);
          return result;
        }
      }
      if (Array.isArray(actual)) {
        // Handle array of symptoms against array of expected values
        let expectedArray: string[];
        if (typeof expected === 'string') {
          expectedArray = expected.split(',').map(item => item.trim());
        } else if (Array.isArray(expected)) {
          expectedArray = expected as string[];
        } else {
          return false;
        }
        
        // Check if any item in actual array matches any item in expected array
        const actualStrings = actual.map(item => String(item).toLowerCase());
        const expectedStrings = expectedArray.map(item => String(item).toLowerCase());
        
        const result = actualStrings.some(actualItem => 
          expectedStrings.some(expectedItem => 
            actualItem.includes(expectedItem) || expectedItem.includes(actualItem)
          )
        );
        
        console.log(`[Rules Debug] Array contains_any check: [${actual.join(', ')}] contains any of [${expectedArray.join(', ')}] = ${result}`);
        return result;
      }
      return false;
    default:
      console.warn(`[Rules Debug] Unknown or unhandled comparator '${comparator}' for parameter '${parameterName}' in main switch.`);
      return false;
  }
}
